/**
 * What a tapped quick action means.
 *
 * This decision used to live inside ChatContainerReader.selectQuickAction. The
 * end-of-reading options now render in their own menu as well, and two copies
 * of "what does Finish Reading do" would drift the first time either changed —
 * so both surfaces read the answer from here.
 *
 * Pure: it decides, the caller performs. The returned descriptor is the full
 * set of consequences, so a caller that ignores a field simply doesn't support
 * that action rather than doing half of it.
 */

import { isReply } from '@/utils/inline-choices'

const NONE = Object.freeze({
  openChatAbout: false,
  // "Keep talking: 5 more" (Talk it through): the app pays for it itself
  // (POST /reader/talk/more/), never as a chat turn.
  talkMore: false,
  showLogin: false,
  exitReading: null,
  clearSuggestions: false,
  preTrigger: null,
  trigger: null,
  // Whether tapping this action dismisses the action list. True for everything
  // that reaches the chat's shared path — including Rate and Share, which
  // dismiss before revealing their row — but not for the front-end-only
  // destinations, which leave the list standing.
  hideQuickActions: false,
  chat: null,
})

/**
 * Buttons that start or steer the reading. Their words are what the button
 * means, not what she said, so they are sent as the turn's value but never
 * shown as her line (R3.5, dev walk 2026-10-15: "I would like to get my tarot
 * reading." appeared as a line she typed when she tapped Select Cards).
 * Chips that answer the Mystic (continue_conversation, the topic starters)
 * are her words and stay.
 */
const COMMANDS = new Set([
  'select_cards', 'select_cards_one_card', 'select_cards_three_card', 'select_cards_four_card',
  'start_reading_conversation', 'reset_conversation', 'get_tarot_reading',
  // Talk it through's "Pull a card for this": a command, not her words.
  'talk_pull_card',
])
export function isCommandAction(action) {
  return COMMANDS.has((action && action.action_type) || '')
}

/** The words those buttons carry, for lines already saved before R3.5. */
const COMMAND_ECHOES = new Set([
  'I would like to get my tarot reading.', "I'd like a single-card reading.",
  "I'd like a three-card reading.", "I'd like a four-card reading.",
  'I’d like to get a tarot reading.', 'I’d like to talk about something else.',
  "I'd like a tarot reading!",
])
export function isCommandEcho(text) {
  return typeof text === 'string' && COMMAND_ECHOES.has(text.trim())
}

export function routeQuickAction(action) {
  const a = action || {}
  const type = a.action_type || ''
  if (!type) return { ...NONE }

  const r = { ...NONE }

  // "Talk it through" (key chat_about_this) opens the reply box even with a
  // reading on screen, so the seeker can answer the Mystic's question.
  if (type === 'chat_about_this') r.openChatAbout = true

  if (type === 'talk_more') {
    r.talkMore = true
    return r
  }

  if (type === 'reveal_myself') {
    r.showLogin = true
    return r
  }

  if (type === 'visit_mystic_aida') {
    r.exitReading = 'iam'
    r.clearSuggestions = true
    return r
  }

  r.trigger = type

  // Community fires its trigger a second time, before the rest of the routing.
  // That is how it has always behaved; it is preserved here rather than
  // quietly corrected, because the panel swap it drives is timing-sensitive
  // and this module's job is to stop the two surfaces drifting, not to change
  // what either of them does.
  if (type === 'visit_mystic_community') r.preTrigger = type

  // Front-end-only destination. A chat turn here would POST the current state
  // to a trigger it doesn't have — visit_quest against TAROT_FUNNEL 500'd.
  if (type === 'visit_quest') return r

  r.hideQuickActions = true

  // Rate and Share open their row locally; neither is a conversation turn.
  if (type === 'rate_reading' || type === 'share_reading') return r

  // Finish Reading routes on its type alone. Echoing its prompt would print
  // "I'm done with my reading." as though the seeker had typed it.
  const isFinish = type === 'exit_reading'
  // Inline text is for replies, chips are tools (Chris, 2026-10-27): only a
  // reply (what she would say next) is said as her words and echoes as her
  // bubble. A tool (Select Cards, Talk it through, a new reading, Change the
  // subject...) does something TO the session: its words go as the turn's
  // value, never as her line.
  const quiet = isFinish || isCommandAction(a) || !isReply(a)
  const instruction = quiet ? '' : (a.prompt_instructions || '')
  r.chat = {
    instruction,
    actionType: type,
    conversationType: a.conversation_type,
    formValue: isFinish ? '' : (a.widget_return_value || a.prompt_instructions || ''),
  }
  return r
}
